import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaShieldAlt, FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import './Doubleinsurance.css';

// ══════════════════════════════════════
//  CONSTANTS
// ══════════════════════════════════════
const API = 'https://digitalapi.definiteassurance.com/api';
const COMPANY = 'Definite';
const DOUBLE_INS_WEBHOOK =
  'https://sms.definiteassurance.com/webhook/d6c9608b-cd90-481a-995d-d40c908c96db';

// ══════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════
const formatDateFromMs = (ms) => {
  const d = new Date(ms);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

// ══════════════════════════════════════
//  COMPONENT
// ══════════════════════════════════════
export default function Doubleinsurance() {
  const navigate = useNavigate();

  // ── Auth & agent ──
  const [credentials, setCredentials] = useState(null);
  const [isAuth, setIsAuth] = useState(false);
  const [agent, setAgent] = useState('');
  const [agentName, setAgentName] = useState('');

  // ── Form ──
  const [reg, setReg] = useState('');
  const [chassis, setChassis] = useState('');
  const [commencementDate, setCommencementDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');

  // ── Result ──
  const [checking, setChecking] = useState(false);
  const [checked, setChecked] = useState(false);
  const [hasDoubleInsurance, setHasDoubleInsurance] = useState(false);
  const [result, setResult] = useState(null);

  // ══════════════════════════════════════
  //  INIT
  // ══════════════════════════════════════
  useEffect(() => {
    const user = localStorage.getItem('definiteagent');
    if (!user) { navigate('/login'); return; }
    setAgent(user);

    const now = Date.now();
    setCommencementDate(formatDateFromMs(now));
    setExpiryDate(formatDateFromMs(now + 31_536_000_000));

    const init = async () => {
      try {
        const credRes = await fetch(`${API}/getCredentials?username=${COMPANY}`);
        const credData = await credRes.json();
        setCredentials(btoa(`${credData.Username}:${credData.Password}`));
        setIsAuth(true);
      } catch (e) {
        console.error('Init failed', e);
        toast.error('Failed to initialise. Please reload.');
      }
    };
    init();
  }, [navigate]);

  // ── Agent name ──
  useEffect(() => {
    if (!agent || !isAuth || !credentials) return;
    fetch(`${API}/getDealerdetails?email=${agent}`, {
      headers: { 'Content-Type': 'application/json', Authorization: `Basic ${credentials}` },
    })
      .then((r) => r.json())
      .then((d) => setAgentName(d.Dealer_name || agent))
      .catch(console.error);
  }, [agent, isAuth, credentials]);

  // ══════════════════════════════════════
  //  CHECK
  // ══════════════════════════════════════
  const checkDoubleInsurance = useCallback(async () => {
    if (!reg.trim() && !chassis.trim()) {
      toast.warning('Please enter a vehicle registration or chassis number.');
      return;
    }
    setChecking(true);
    setChecked(false);
    setResult(null);
    try {
      const { data } = await axios.post(DOUBLE_INS_WEBHOOK, {
        chasis: chassis.trim(),
        reg: reg.trim(),
        commence: commencementDate,
        end: expiryDate,
      });
      setChecked(true);
      if (data.success) {
        setHasDoubleInsurance(true);
        setResult({
          certNo: data.certNo,
          company: data.company,
          endDate: data.endDate,
          chassis: data.chassis,
          reg: data.reg,
        });
      } else {
        setHasDoubleInsurance(false);
      }
    } catch (e) {
      console.error('Double insurance check failed', e);
      toast.error('Check failed. Please try again.');
    } finally {
      setChecking(false);
    }
  }, [reg, chassis, commencementDate, expiryDate]);

  const handleReset = () => {
    setReg('');
    setChassis('');
    setChecked(false);
    setResult(null);
    setHasDoubleInsurance(false);
  };

  // ══════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════
  return (
    <div className="di-page">
      <Header userName={agentName || agent} />
      <Sidebar />
      <ToastContainer position="top-right" autoClose={3000} />

      <main className="di-main">
        <div className="di-top-row">
          <span className="di-company">DEFINITE ASSURANCE</span>
          <span className="di-breadcrumb">Validate / Double Insurance</span>
        </div>

        <div className="di-card">
          <div className="di-card-header">
            <FaShieldAlt className="di-card-icon" />
            <h3>Check Vehicle Double Insurance</h3>
          </div>

          <div className="di-card-body">
            {/* ── Inputs ── */}
            <div className="di-form-grid">
              <div className="di-form-group">
                <label htmlFor="di-reg">Vehicle Registration</label>
                <input
                  id="di-reg"
                  type="text"
                  placeholder="e.g. KDA 123A"
                  value={reg}
                  onChange={(e) => setReg(e.target.value.toUpperCase())}
                  disabled={checking}
                />
              </div>
              <div className="di-form-group">
                <label htmlFor="di-chassis">Chassis Number</label>
                <input
                  id="di-chassis"
                  type="text"
                  placeholder="e.g. JTMRFREV0FD123456"
                  value={chassis}
                  onChange={(e) => setChassis(e.target.value.toUpperCase())}
                  disabled={checking}
                />
              </div>
            </div>

            {/* ── Date context ── */}
            <div className="di-date-row">
              <span><strong>Cover from:</strong> {commencementDate}</span>
              <span><strong>Cover to:</strong> {expiryDate}</span>
            </div>

            {/* ── Actions ── */}
            <div className="di-actions">
              <button
                className="di-btn di-btn-primary"
                onClick={checkDoubleInsurance}
                disabled={checking}
              >
                {checking ? <><FaSpinner className="di-spinner" /> Checking…</> : <><FaSearch /> Check</>}
              </button>
              {checked && (
                <button className="di-btn di-btn-secondary" onClick={handleReset}>
                  Reset
                </button>
              )}
            </div>

            {/* ── Result ── */}
            {checked && (
              <div className={`di-result ${hasDoubleInsurance ? 'di-result-warning' : 'di-result-clear'}`}>
                {hasDoubleInsurance ? (
                  <>
                    <div className="di-result-title">
                      <FaTimesCircle className="di-result-icon" />
                      <strong>Double Insurance Detected</strong>
                    </div>
                    <p className="di-result-subtitle">
                      This vehicle already has an active policy with the following details:
                    </p>
                    <div className="di-result-grid">
                      {[
                        { label: 'Registration',   value: result?.reg },
                        { label: 'Chassis',         value: result?.chassis },
                        { label: 'Insurer',         value: result?.company },
                        { label: 'Certificate No.', value: result?.certNo },
                        { label: 'Cover Expiry',    value: result?.endDate },
                      ].map(({ label, value }) => (
                        <div key={label} className="di-result-item">
                          <span className="di-result-label">{label}</span>
                          <span className="di-result-value">{value || '—'}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="di-result-title">
                    <FaCheckCircle className="di-result-icon" />
                    <strong>No Double Insurance Found</strong>
                    <span className="di-result-subtitle-inline">
                      — This vehicle has no active conflicting cover.
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
